import { useTheme } from '@shopify/restyle';
import { NavigationRouteContext } from '@react-navigation/native';
import type { DomainError } from 'app/lib/axios/types';
import type { AxiosResponse } from 'axios';
import { AxiosError } from 'axios';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Animated, { FadeInUp, FadeOutDown } from 'react-native-reanimated';
import { IconAlertTriangle } from '@tabler/icons-react-native';

import { Text } from './text';
import type { Theme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

interface RemoteErrorProps {
  error?: Error;
  message?: string;
}

export function RemoteError({ error, message }: RemoteErrorProps) {
  // Some errors are rendered from overlays/sheets that are mounted outside a
  // navigation screen (for example, the global login sheet). React Navigation
  // throws when useRoute is used without a route context, so keep route-specific
  // lookup optional and fall back to common translations.
  const route = useContext(NavigationRouteContext);
  const routeName = route?.name;
  const theme = useTheme<Theme>();
  const { t, i18n } = useTranslation();

  return (
    <Animated.View
      entering={FadeInUp.duration(150)}
      exiting={FadeOutDown.duration(50)}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.error.default']} size={24} />

      <Text
        accessibilityRole="alert"
        color="surface.error.default"
        flex={1}
        flexShrink={1}
        marginStart="spacing.general.x1"
        minWidth={0}
      >
        {message ??
          (!isDomainError(error!)
            ? t('genericError', { ns: 'common' })
            : routeName && i18n.exists(`errors.${error.response.data.id}`, { ns: routeName })
              ? t(`errors.${error.response.data.id}`, { ns: routeName })
              : i18n.exists(`errors.${error.response.data.id}`, { ns: 'common' })
                ? t(`errors.${error.response.data.id}`, { ns: 'common' })
                : t('genericError', { ns: 'common' }))}
      </Text>
    </Animated.View>
  );
}

function isDomainError(e: Error): e is AxiosError & { response: AxiosResponse<DomainError> } {
  return e instanceof AxiosError && e.response?.data.id;
}
